import { FadeUp } from '../primitives/FadeUp';
import { SectionH2 } from '../primitives/SectionH2';
import { SectionLabel } from '../primitives/SectionLabel';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  ACCENT,
  BORDER,
  EASE,
  FONT_DISPLAY,
  FONT_MONO,
  SURFACE,
  SURFACE2,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
} from '../../tokens';

const TRACK_A: readonly string[] = [
  'Posts when there is time, which is never the busy weeks',
  'Leads sit until the end of the day, then get a cold reply',
  'Reviews asked for occasionally, when it is not awkward',
  'Ads boosted on instinct, results never traced back to a cause',
  'Starts from zero every time it stops',
];

const TRACK_B: readonly string[] = [
  'Publishes on a fixed cadence whether the week is busy or not',
  'Answers inside a minute, while the intent is still live',
  'Asks for a review on every completed job, automatically',
  'Ads cut from the content already proven to land',
  'Compounds, because the loop keeps what worked',
];

interface TrackCardProps {
  label: string;
  title: string;
  points: readonly string[];
  /** Track B is the desirable one, so it carries the accent treatment. */
  accent: boolean;
  isMobile: boolean;
}

function TrackCard({ label, title, points, accent, isMobile }: TrackCardProps) {
  const restingBorder = accent ? 'rgba(255,107,0,0.25)' : BORDER;
  const restingShadow = accent ? '0 0 40px rgba(255,107,0,0.06)' : 'none';

  return (
    <div
      style={{
        background: SURFACE,
        border: `1px solid ${restingBorder}`,
        borderRadius: 20,
        padding: isMobile ? 28 : 36,
        height: '100%',
        boxShadow: restingShadow,
        transition: `border-color 0.3s, transform 0.35s ${EASE}, box-shadow 0.35s`,
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget;
        el.style.borderColor = accent ? 'rgba(255,107,0,0.5)' : 'rgba(255,107,0,0.25)';
        el.style.transform = 'translateY(-5px) scale(1.015)';
        el.style.boxShadow = accent ? '0 16px 48px rgba(255,107,0,0.15)' : '0 16px 48px rgba(255,107,0,0.1)';
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget;
        el.style.borderColor = restingBorder;
        el.style.transform = 'translateY(0) scale(1)';
        el.style.boxShadow = restingShadow;
      }}
    >
      <div
        style={{
          fontFamily: FONT_MONO,
          fontSize: '0.62rem',
          color: accent ? ACCENT : '#666',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          marginBottom: 16,
        }}
      >
        {label}
      </div>
      <h3
        style={{
          fontFamily: FONT_DISPLAY,
          fontSize: '1.6rem',
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          color: accent ? ACCENT : '#666',
          marginBottom: 16,
        }}
      >
        {title}
      </h3>
      <ul style={{ color: TEXT_SECONDARY, fontSize: '0.9rem', lineHeight: 2, paddingLeft: 20, margin: 0 }}>
        {points.map((point) => (
          <li key={point}>{point}</li>
        ))}
      </ul>
    </div>
  );
}

export function TwoTrackEconomy() {
  const isMobile = useIsMobile();

  return (
    <section style={{ padding: isMobile ? '60px 20px' : '100px 64px', background: 'rgba(6,6,6,0.88)' }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <FadeUp>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <SectionLabel number="-">The Bigger Picture</SectionLabel>
            <SectionH2 style={{ marginBottom: 16 }}>Two Businesses. Same Market. Different Speed.</SectionH2>
            <p
              style={{
                color: TEXT_SECONDARY,
                maxWidth: 600,
                margin: '0 auto',
                lineHeight: 1.75,
                fontSize: '1.05rem',
              }}
            >
              The gap between these two is not talent and it is not budget, it is whether the work
              happens when the week gets busy. That difference compounds quietly, one quarter at a time,
              until one of them looks like the obvious choice and nobody can quite say when that happened.
            </p>
          </div>
        </FadeUp>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: 24,
            marginBottom: 40,
          }}
        >
          <FadeUp delay={60}>
            <TrackCard
              label="Track A"
              title="The Ones Doing It Manually"
              points={TRACK_A}
              accent={false}
              isMobile={isMobile}
            />
          </FadeUp>

          <FadeUp delay={120}>
            <TrackCard
              label="Track B"
              title="The Ones Running A System"
              points={TRACK_B}
              accent
              isMobile={isMobile}
            />
          </FadeUp>
        </div>

        <FadeUp delay={160}>
          <div
            style={{
              textAlign: 'center',
              background: SURFACE2,
              border: `1px solid ${BORDER}`,
              borderRadius: 20,
              padding: isMobile ? 28 : 40,
            }}
          >
            <p
              style={{
                color: TEXT_PRIMARY,
                fontSize: '1.1rem',
                fontWeight: 600,
                lineHeight: 1.75,
                maxWidth: 640,
                margin: '0 auto',
              }}
            >
              Most businesses are on the first track by accident rather than by decision. The only
              question worth answering is which one you are on right now, and whether you picked it.
            </p>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
